<template>
  <Teleport to="body">
    <div
      v-if="previewItem"
      class="fixed inset-0 z-[10000] flex cursor-zoom-out items-center justify-center bg-black/50 p-8"
      data-testid="api-feed-item-preview"
      @click.self="closePreview"
    >
      <img
        :src="previewSrc"
        :alt="previewItem.name"
        class="max-h-[80vh] max-w-[75vw] rounded-lg bg-white object-contain shadow-2xl"
        @click="closePreview"
      />
    </div>
  </Teleport>

  <TModal
    lg
    header-low
    :header="
      replaceMode
        ? $t('api_block.replace_item_modal_title')
        : $t('api_block.select_api_block')
    "
    data-testid="api-feed-items-selection"
    @close="$emit('close')"
  >
    <template #dialog-body>
      <div class="space-y-3 p-5 pt-0">
        <div
          v-if="!blockProperties?.apiWithoutSearch"
          class="flex items-center gap-1 rounded-md border border-solid border-transparent !bg-gray-600 px-2.5 py-1 font-inter font-medium leading-[1] text-gray-100 focus-within:outline focus-within:outline-2 focus-within:outline-accent-1"
        >
          <SearchIcon />
          <input
            ref="queryInput"
            v-model="searchQuery"
            data-testid="search-api-item"
            :placeholder="$t('api_block.search_items')"
            type="text"
            class="rounded-0 box-border block w-full border-none bg-gray-600 p-1 font-inter text-xs leading-[16px] text-gray-50 !outline-none transition duration-300 placeholder:text-gray-100 focus:!text-gray-10"
          />
          <button
            v-if="searchQuery"
            type="button"
            data-testid="clear-search-api-item"
            :aria-label="$t('actions.clear')"
            class="flex flex-shrink-0 cursor-pointer items-center justify-center rounded border-none bg-transparent p-0.5 text-gray-100 transition hover:text-gray-50"
            @click="searchQuery = ''"
          >
            <Close class="h-4 w-4" />
          </button>
        </div>
        <div :class="{ 'flex items-center gap-2': hasCategoriesURL }">
          <TAsyncDropdown
            data-testid="product-feed-autocomplete"
            :data="apiBlock.feedsData.value"
            :selected="apiBlock.selectedFeed.value"
            :placeholder="
              !apiBlock.feeds.value && !apiBlock.feedLoading.value
                ? $t('api_block.no_feeds')
                : ''
            "
            :label="$t('api_block.feed')"
            :loading="apiBlock.feedLoading.value"
            data-label="name"
            data-key="id"
            :show-search="!blockProperties?.apiWithoutSearch"
            :should-load-more="
              blockProperties?.apiWithoutSearch
                ? apiBlock.nextFeedKey.value !== undefined
                : false
            "
            :no-data-text="$t('product.no_data_found')"
            :error-input="!apiBlock.feeds.value && !apiBlock.feedLoading.value"
            :error-text="$t('api_block.no_feeds_connected')"
            :full-width="!hasCategoriesURL"
            @search="searchFeeds"
            @selection-changed="handleSelectFeed"
            @load-more="loadMoreFeeds"
          />

          <TAsyncDropdown
            v-if="hasCategoriesURL"
            ref="categoryDropdownRef"
            data-testid="category-feed-autocomplete"
            :data="categoriesDropdownData"
            :selected="selectedCategory"
            :placeholder="$t('api_block.all_categories')"
            :label="$t('api_block.category')"
            :loading="apiBlock.categoryLoading.value"
            data-label="name"
            data-key="id"
            show-search
            :no-data-text="$t('product.no_data_found')"
            @search="searchCategories"
            @selection-changed="onCategoryChanged"
            @load-more="loadMoreCategories"
          >
          </TAsyncDropdown>
        </div>

        <!-- Tabs -->
        <TTabsV2
          v-if="!replaceMode"
          data-testid="tabs-for-all-vs-select"
          :options="tabOptions"
          :defult-active-index="0"
          @on-tab-change="onTabChange"
        />
      </div>

      <div
        v-if="!replaceMode && activeTab === 0 && displayedItems.length > 0"
        class="flex items-center justify-end px-5"
      >
        <button
          data-testid="select-all"
          class="cursor-pointer bg-transparent font-inter text-xs text-gray-100 transition hover:text-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
          :disabled="areAllDisplayedSelected || !canSelectMoreDisplayed"
          @click="selectAllDisplayed"
        >
          {{ $t("api_block.select_all") }}
        </button>
      </div>

      <!-- Item list -->
      <component
        :is="blockProperties?.apiWithoutSearch ? 'PerfectScrollbar' : 'div'"
        :key="activeTab"
        class="space-y-2 px-5 pt-2 font-inter text-xs text-gray-200"
        :class="{ 'min-h-[360px]': !blockProperties?.apiWithoutSearch }"
        :style="
          blockProperties?.apiWithoutSearch ? { height: '360px' } : undefined
        "
      >
        <div
          v-for="item in displayedItems"
          :key="item.id"
          class="group flex cursor-pointer items-center gap-3 rounded-md p-2 transition duration-300 hover:bg-gray-500"
          :class="{
            'bg-gray-500': isSelected(item.id),
            'bg-gray-700': !isSelected(item.id),
          }"
          data-testid="api-feed-item"
          @click="toggleItem(item)"
        >
          <TCheckBox
            :model-value="isSelected(item.id)"
            @update:model-value="toggleItem(item)"
            @click.stop
          />

          <div
            class="group/img relative h-12 w-12 flex-shrink-0 cursor-zoom-in overflow-hidden rounded-md border border-gray-500"
            data-testid="api-feed-item-image"
            @click.stop="openPreview(item)"
          >
            <img
              :src="
                item.image ||
                item.img_url ||
                `${awsFolder}/assets/images/merge-tags/dummy.svg`
              "
              class="h-full w-full object-cover"
              :alt="item.name"
            />
            <div
              class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity duration-200 group-hover/img:opacity-100"
            >
              <MagnifyingGlassPlus class="h-4 w-4 text-white" />
            </div>
          </div>

          <div class="flex min-w-0 flex-1 flex-col">
            <span
              class="truncate font-medium transition duration-300 group-hover:text-gray-50"
              :class="{
                'text-gray-50': isSelected(item.id),
                'text-gray-100': !isSelected(item.id),
              }"
            >
              {{ item.name }}
            </span>
            <span
              class="text-[11px] transition duration-300 group-hover:text-gray-100"
              :class="{
                'text-gray-100': isSelected(item.id),
                'text-gray-200': !isSelected(item.id),
              }"
            >
              ID: {{ item.custom_id || item.id }}
            </span>
          </div>
        </div>

        <TLoadingSpinner v-if="apiBlock.itemLoading.value" />

        <div
          v-if="
            activeTab === 0 &&
            !apiBlock.itemLoading.value &&
            displayedItems.length === 0
          "
          class="py-8 text-center text-xs text-gray-200"
        >
          {{ $t("product.no_data_found") }}
        </div>

        <div
          v-if="activeTab === 1 && selectedItems.length === 0"
          class="py-8 text-center text-xs text-gray-200"
        >
          {{ $t("api_block.no_items_selected") }}
        </div>

        <div
          v-else-if="
            activeTab === 1 &&
            selectedItems.length > 0 &&
            filteredSelectedItems.length === 0
          "
          class="py-8 text-center text-xs text-gray-200"
        >
          {{ $t("product.no_data_found") }}
        </div>
      </component>

      <!-- Pagination -->
      <div
        class="mx-5 mt-3 flex items-center justify-end gap-3 border-t border-solid border-transparent border-t-gray-400 py-3 text-xs text-gray-200"
      >
        <template v-if="activeTab === 0 && blockProperties?.apiWithoutSearch">
          <button
            v-if="apiBlock.nextArticleKey.value !== undefined"
            data-testid="load-more"
            class="cursor-pointer bg-transparent font-inter text-gray-100 transition hover:text-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            :disabled="apiBlock.itemLoading.value"
            @click="loadMoreItems"
          >
            {{ $t("api_block.load_more") }}
          </button>
        </template>
        <template v-else>
          <button
            data-testid="prev-page"
            class="cursor-pointer bg-transparent font-inter text-gray-100 transition hover:text-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            :disabled="isPrevDisabled"
            @click="prevPage"
          >
            &laquo; {{ $t("api_block.prev") }}
          </button>
          <button
            data-testid="next-page"
            class="cursor-pointer bg-transparent font-inter text-gray-100 transition hover:text-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            :disabled="isNextDisabled"
            @click="nextPage"
          >
            {{ $t("api_block.next") }} &raquo;
          </button>
        </template>
      </div>
    </template>

    <template #dialog-footer>
      <div class="px-5 pb-3">
        <div class="flex w-full items-center justify-between gap-3">
          <button
            v-if="!replaceMode && selectedItems.length > 0"
            data-testid="deselect-all"
            class="cursor-pointer bg-transparent font-inter text-xs text-gray-100 transition hover:text-gray-50"
            @click="deselectAll"
          >
            {{ $t("api_block.deselect_all", { count: selectedItems.length }) }}
          </button>
          <span v-else></span>
          <div class="flex items-center gap-3">
            <TButton
              data-testid="cancel-feed-items-selection"
              @click="$emit('close')"
            >
              {{ $t("actions.cancel") }}
            </TButton>
            <TButton
              data-testid="select-feed-items"
              accent
              :disabled="selectedItems.length === 0"
              @click="handleAdd"
            >
              <template v-if="replaceMode">
                {{ $t("api_block.replace_item_action") }}
              </template>
              <template v-else>
                {{ $t("api_block.add_items", { name: pluralName }) }}
                <template v-if="selectedItems.length > 0">
                  ({{ selectedItems.length }})
                </template>
              </template>
            </TButton>
          </div>
        </div>
      </div>
    </template>
  </TModal>
</template>

<script setup lang="ts">
  import useApiBlock from "@/composables/useApiBlock";
  import { useApiBlocksStore } from "@/stores/apiBlocks";
  import { IData } from "types/AutoComplete/IData";
  import {
    TModal,
    TAsyncDropdown,
    TTabsV2,
    TCheckBox,
    TButton,
    TLoadingSpinner,
  } from "@topol/twig-ui";
  import { SearchIcon, MagnifyingGlassPlus, Close } from "@topol/icons";
  import { useDebounceFn } from "@vueuse/core";
  import { useI18n } from "vue-i18n";
  import { computed, onMounted, ref, watch } from "vue";

  interface FeedData extends IData {
    id: string;
    name: string;
  }

  const props = defineProps<{
    blockName: string;
    maxItems?: number;
    selection?: any[];
    replaceMode?: boolean;
  }>();

  const emit = defineEmits<{
    (e: "select", items: any[]): void;
    (e: "close"): void;
  }>();

  const { t } = useI18n();
  const apiBlocksStore = useApiBlocksStore();
  const { apiBlock } = useApiBlock(props.blockName);

  const lastFeedStorageKey = `topol:lastSelectedFeed:${props.blockName}`;

  function readLastFeedId(): string | undefined {
    try {
      return localStorage.getItem(lastFeedStorageKey) ?? undefined;
    } catch {
      return undefined;
    }
  }

  function rememberFeedId(id: string) {
    try {
      localStorage.setItem(lastFeedStorageKey, id);
    } catch {
      // localStorage unavailable (private mode, quota) — skip silently
    }
  }

  const searchQuery = ref("");
  const selectedItems = ref<any[]>(
    JSON.parse(JSON.stringify(props.selection)) ?? []
  );
  const activeTab = ref(0);
  const apiCurrentPage = ref(1);
  const selectedCurrentPage = ref(1);
  const selectedCategory = ref<any>({ id: "", name: "" });
  const categoryQuery = ref("");
  const appliedCategoryQuery = ref("");
  const categoryDropdownRef = ref<any>(null);
  const previewItem = ref<any>(null);

  const previewSrc = computed(() => {
    if (!previewItem.value) return "";
    return (
      previewItem.value.image ||
      previewItem.value.img_url ||
      `${awsFolder.value}/assets/images/merge-tags/dummy.svg`
    );
  });

  function openPreview(item: any) {
    previewItem.value = item;
  }

  function closePreview() {
    previewItem.value = null;
  }

  const awsFolder = computed(() => {
    return import.meta.env.VITE_APP_AWS_FOLDER;
  });

  const blockProperties = computed(() =>
    apiBlocksStore.blocks.get(props.blockName)
  );

  const pluralName = computed(
    () => blockProperties.value?.pluralName ?? "Items"
  );

  const hasCategoriesURL = computed(
    () => !!blockProperties.value?.categoriesURL
  );

  const tabOptions = computed(() => [
    {
      name: `${t("api_block.all_items", { name: pluralName.value })}`,
      value: "all",
    },
    {
      name: `${t("api_block.selected_items", { name: pluralName.value })} (${selectedItems.value.length})`,
      value: "selected",
    },
  ]);

  const filteredSelectedItems = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return selectedItems.value;
    return selectedItems.value.filter((i) => {
      const name = String(i.name ?? "").toLowerCase();
      const id = String(i.custom_id ?? i.id ?? "").toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  });

  const displayedItems = computed(() => {
    if (activeTab.value === 1) {
      const start = (selectedCurrentPage.value - 1) * apiBlock.productPerPage;
      const end = start + apiBlock.productPerPage;
      return filteredSelectedItems.value.slice(start, end);
    }
    return apiBlock.itemsData.value;
  });

  const totalPages = computed(() => {
    if (activeTab.value === 1) {
      return Math.max(
        1,
        Math.ceil(filteredSelectedItems.value.length / apiBlock.productPerPage)
      );
    }
    if (!apiBlock.items.value) return 1;
    return Math.ceil(
      apiBlock.items.value.total_records / apiBlock.productPerPage
    );
  });

  const hasNextPage = computed(() => {
    if (activeTab.value === 1) {
      return selectedCurrentPage.value < totalPages.value;
    }
    return apiCurrentPage.value < totalPages.value;
  });

  const isPrevDisabled = computed(() => {
    if (activeTab.value === 1) return selectedCurrentPage.value <= 1;
    return apiCurrentPage.value <= 1 || apiBlock.itemLoading.value;
  });

  const isNextDisabled = computed(() => {
    if (activeTab.value === 1) return !hasNextPage.value;
    return !hasNextPage.value || apiBlock.itemLoading.value;
  });

  const categoriesDropdownData = computed(() => {
    if (appliedCategoryQuery.value.trim()) {
      return apiBlock.categoriesData.value;
    }
    const allOption = { id: "", name: t("api_block.all_categories") };
    return [allOption, ...apiBlock.categoriesData.value];
  });

  const isAtMaxSelection = computed(
    () =>
      props.maxItems !== undefined &&
      selectedItems.value.length >= props.maxItems
  );

  function isSelected(id: string) {
    return selectedItems.value.some((i) => i.id === id);
  }

  function toggleItem(item: any) {
    if (props.replaceMode) {
      selectedItems.value = [item];
      return;
    }
    const idx = selectedItems.value.findIndex((i) => i.id === item.id);
    if (idx >= 0) {
      selectedItems.value.splice(idx, 1);
    } else if (!isAtMaxSelection.value) {
      selectedItems.value.push(item);
    }
  }

  const areAllDisplayedSelected = computed(
    () =>
      displayedItems.value.length > 0 &&
      displayedItems.value.every((i) => isSelected(i.id))
  );

  const canSelectMoreDisplayed = computed(() => {
    if (props.maxItems === undefined) return true;
    const unselectedCount = displayedItems.value.filter(
      (i) => !isSelected(i.id)
    ).length;
    const remaining = props.maxItems - selectedItems.value.length;
    return remaining > 0 && unselectedCount > 0;
  });

  function selectAllDisplayed() {
    for (const item of displayedItems.value) {
      if (isSelected(item.id)) continue;
      if (isAtMaxSelection.value) break;
      selectedItems.value.push(item);
    }
  }

  function deselectAll() {
    selectedItems.value = [];
  }

  function onTabChange(tab: any) {
    activeTab.value = tab;
    if (activeTab.value === 1 && totalPages.value < selectedCurrentPage.value) {
      selectedCurrentPage.value = 1;
    } else if (
      activeTab.value === 0 &&
      totalPages.value < apiCurrentPage.value
    ) {
      apiCurrentPage.value = 1;
    }
  }

  async function handleSelectFeed(feed: FeedData) {
    apiBlock.activeFeed.value = feed.id;
    apiCurrentPage.value = 1;
    if (feed.name) {
      apiBlock.selectedFeed.value = feed;
      apiBlock.feedQuery.value = feed.name;
    }
    if (feed.id) {
      rememberFeedId(feed.id);
    }

    //reset item query without watch trigger
    apiBlock.isInitItem.value = true;
    apiBlock.itemQuery.value = "";
    apiBlock.selectedItem.value = undefined;

    apiBlock.itemsData.value = [];
    apiBlock.items.value = null;
    apiBlock.nextArticleKey.value = undefined;

    if (hasCategoriesURL.value) {
      apiBlock.categoriesData.value = [];
      apiBlock.categories.value = null;
      selectedCategory.value = { id: "", dataLabel: "" };
      apiBlock.activeCategory.value = null;
      await apiBlock.getCategories(undefined, 1);
    }

    await apiBlock.getItems(undefined, undefined, getProductCurrentPage());
  }

  function getProductCurrentPage() {
    if (apiBlock.items.value && apiBlock.items.value.current_page) {
      return apiBlock.items.value.current_page;
    }

    return 1;
  }

  async function onCategoryChanged(category: any) {
    selectedCategory.value = category;
    categoryQuery.value = category.name ?? "";
    apiBlock.activeCategory.value = category.id || null;
    apiCurrentPage.value = 1;

    apiBlock.itemsData.value = [];
    apiBlock.items.value = null;

    await apiBlock.getItems(undefined, searchQuery.value || undefined, 1);
  }

  async function loadMoreFeeds() {
    const nextPage = apiBlock.feeds.value
      ? apiBlock.feeds.value.current_page + 1
      : 1;
    await apiBlock.getFeeds(
      apiBlock.feedQuery.value || undefined,
      undefined,
      nextPage
    );
  }

  async function loadMoreItems() {
    await apiBlock.getItems(undefined, undefined, undefined);
  }

  async function loadMoreCategories() {
    const nextPage = apiBlock.categories.value
      ? apiBlock.categories.value.current_page + 1
      : 1;
    await apiBlock.getCategories(categoryQuery.value || undefined, nextPage);
  }

  const searchCategories = (query: string) => {
    categoryQuery.value = query;
    debouncedCategorySearch(query);
  };

  const debouncedCategorySearch = useDebounceFn(async (query: string) => {
    apiBlock.categoriesData.value = [];
    apiBlock.categories.value = null;
    await apiBlock.getCategories(query || undefined, 1);
    appliedCategoryQuery.value = query;
  }, 300);

  const debouncedFeedSearch = useDebounceFn(async (query: string) => {
    apiBlock.feedsData.value = [];
    apiBlock.feeds.value = null;
    await apiBlock.getFeeds(query || undefined, undefined, 1);
  }, 300);

  const searchFeeds = (query: string) => {
    apiBlock.feedQuery.value = query;
    debouncedFeedSearch(query);
  };

  async function nextPage() {
    if (isNextDisabled.value) return;
    if (activeTab.value === 1) {
      selectedCurrentPage.value++;
      return;
    }
    apiCurrentPage.value++;
    apiBlock.itemsData.value = [];
    await apiBlock.getItems(
      undefined,
      searchQuery.value || undefined,
      apiCurrentPage.value
    );
  }

  async function prevPage() {
    if (isPrevDisabled.value) return;
    if (activeTab.value === 1) {
      selectedCurrentPage.value--;
      return;
    }
    apiCurrentPage.value--;
    apiBlock.itemsData.value = [];
    await apiBlock.getItems(
      undefined,
      searchQuery.value || undefined,
      apiCurrentPage.value
    );
  }

  function handleAdd() {
    emit("select", [...selectedItems.value]);
    emit("close");
  }

  const debouncedSearch = useDebounceFn(async (query: string) => {
    apiCurrentPage.value = 1;
    apiBlock.itemsData.value = [];
    apiBlock.items.value = null;
    await apiBlock.getItems(undefined, query || undefined, 1);
  }, 300);

  watch(searchQuery, (newVal) => {
    if (activeTab.value === 1) {
      selectedCurrentPage.value = 1;
      return;
    }
    debouncedSearch(newVal);
  });

  onMounted(async () => {
    const preferredFeedId = readLastFeedId();

    // Fetch feeds
    await apiBlock.getFeeds(undefined, preferredFeedId, 1);

    // Auto-select feed
    if (apiBlock.feedsData.value.length > 0) {
      const targetFeed = preferredFeedId
        ? (apiBlock.feedsData.value.find(
            (f) => f.id.toString() === preferredFeedId
          ) ?? apiBlock.feedsData.value[0])
        : apiBlock.feedsData.value[0];

      if (targetFeed) {
        await handleSelectFeed(targetFeed);
      }
    }
  });
</script>
